import React,{useEffect,useMemo,useState}from"react";
import{Autocomplete,Box,Button,CircularProgress,MenuItem,Paper,Table,TableBody,TableCell,TableContainer,TableHead,TableRow,TextField,Typography}from"@mui/material";
import Sidebar from"../AdminPanel/Sidebar";
import TopBar from"../AdminPanel/TopBar";
import{useDashboardTheme}from"../../../context/DashboardThemeContext";
import{useAlert}from"../../../context/AlertContext";
import{useAuth}from"../../../context/AuthContext";

function formatNumber(value){
  const number=Number(value||0);
  return Number.isFinite(number)
    ?number.toLocaleString("es-EC",{minimumFractionDigits:0,maximumFractionDigits:6})
    :"0";
}

function fieldSx(dashboardTheme){
  return{
    "& .MuiOutlinedInput-root":{
      borderRadius:"8px",
      color:dashboardTheme.text_color,
      bgcolor:dashboardTheme.input_background||dashboardTheme.card_background,
      "& fieldset":{borderColor:dashboardTheme.border_color},
      "&:hover fieldset":{borderColor:dashboardTheme.primary_color},
      "&.Mui-focused fieldset":{borderColor:dashboardTheme.primary_color}
    },
    "& .MuiInputLabel-root":{color:dashboardTheme.muted_text_color},
    "& .MuiInputLabel-root.Mui-focused":{color:dashboardTheme.primary_color},
    "& .MuiSvgIcon-root":{color:dashboardTheme.text_color},
    "& input":{color:dashboardTheme.text_color},
    "& textarea":{color:dashboardTheme.text_color}
  };
}

function Row({label,children,dashboardTheme}){
  return(
    <Box sx={{
      display:"grid",
      gridTemplateColumns:{xs:"1fr",md:"230px minmax(0,1fr)"},
      borderBottom:`1px solid ${dashboardTheme.border_color}`
    }}>
      <Box sx={{
        bgcolor:dashboardTheme.theme_mode==="dark"
          ?"rgba(255,255,255,0.04)"
          :"rgba(17,24,39,0.035)",
        px:{xs:1.5,md:2},
        py:1.25,
        display:"flex",
        alignItems:"center"
      }}>
        <Typography sx={{fontSize:14,fontWeight:700,color:dashboardTheme.text_color}}>
          {label}
        </Typography>
      </Box>

      <Box sx={{px:{xs:1.2,md:1.4},py:1,display:"flex",alignItems:"center",minWidth:0}}>
        {children}
      </Box>
    </Box>
  );
}

export default function NewMovement({empresaId,onBack,onCreated}){
  const[sidebarOpen,setSidebarOpen]=useState(true);
  const{dashboardTheme}=useDashboardTheme();
  const{showSuccess,showError}=useAlert();
  const auth=useAuth();

  const token=auth?.accessToken||localStorage.getItem("accessToken")||"";
  const user=auth?.user||null;

  const[fecha,setFecha]=useState("");
  const[tipo,setTipo]=useState("positivo");
  const[reason,setReason]=useState("");
  const[products,setProducts]=useState([]);
  const[productSearch,setProductSearch]=useState("");
  const[selectedProduct,setSelectedProduct]=useState(null);
  const[loadingProducts,setLoadingProducts]=useState(false);
  const[items,setItems]=useState([]);
  const[saving,setSaving]=useState(false);

  useEffect(()=>{
    const now=new Date();

    const localDate=new Intl.DateTimeFormat("en-CA",{
      year:"numeric",
      month:"2-digit",
      day:"2-digit"
    }).format(now);

    const localTime=now.toLocaleTimeString("es-EC",{
      hour:"2-digit",
      minute:"2-digit"
    });

    setFecha(`${localDate} ${localTime}`);
  },[]);

  const responsable=useMemo(()=>{
    return user?.name||user?.nombre||user?.email||"Usuario actual";
  },[user]);

  const searchProducts=async(query="")=>{
    try{
      setLoadingProducts(true);

      const params=new URLSearchParams();

      if(query.trim()){
        params.append("q",query.trim());
      }

      const response=await fetch(`/api/inventory/products/search/?${params.toString()}`,{
        headers:{
          Accept:"application/json",
          Authorization:`Bearer ${token}`
        }
      });

      const data=await response.json();

      if(!response.ok){
        setProducts([]);
        return;
      }

      const existentes=new Set(items.map(item=>String(item.product_id)));

      setProducts(
        (data.results||[]).filter(
          product=>!existentes.has(String(product.id))
        )
      );
    }catch{
      setProducts([]);
    }finally{
      setLoadingProducts(false);
    }
  };

  useEffect(()=>{
    const delay=setTimeout(()=>{
      searchProducts(productSearch);
    },300);

    return()=>clearTimeout(delay);
  },[productSearch,items]);

  const addProduct=product=>{
    if(!product)return;

    const exists=items.some(
      item=>String(item.product_id)===String(product.id)
    );

    if(exists){
      showError("Este producto ya fue añadido.");
      return;
    }

    setItems(prev=>[
      ...prev,
      {
        uid:`${product.id}-${Date.now()}`,
        product_id:product.id,
        name:product.name,
        code:product.code||"",
        barcode:product.barcode||"",
        codigo_auxiliar:product.codigo_auxiliar||"",
        stock:Number(product.stock||0),
        stock_minimo:Number(product.stock_minimo||0),
        quantity:"1"
      }
    ]);

    setSelectedProduct(null);
    setProductSearch("");
  };

  const updateQuantity=(uid,value)=>{
    setItems(prev=>prev.map(item=>
      item.uid===uid
        ?{...item,quantity:value}
        :item
    ));
  };

  const removeItem=uid=>{
    setItems(prev=>prev.filter(item=>item.uid!==uid));
  };

  const getStockResultante=item=>{
    const stock=Number(item.stock||0);
    const quantity=Number(item.quantity||0);

    if(tipo==="positivo"){
      return stock+quantity;
    }

    return stock-quantity;
  };

  const invalidNegative=useMemo(()=>{
    if(tipo!=="negativo")return false;

    return items.some(item=>
      Number(item.quantity||0)>Number(item.stock||0)
    );
  },[items,tipo]);

  const canSubmit=useMemo(()=>{
    if(!empresaId)return false;
    if(!reason.trim())return false;
    if(!items.length)return false;
    if(items.some(item=>Number(item.quantity||0)<=0))return false;
    if(invalidNegative)return false;
    return true;
  },[empresaId,reason,items,invalidNegative]);

  const save=async()=>{
    if(!empresaId){
      showError("No se pudo determinar la empresa.");
      return;
    }

    if(!items.length){
      showError("Debe añadir al menos un producto.");
      return;
    }

    if(!reason.trim()){
      showError("Debe indicar el motivo del movimiento.");
      return;
    }

    if(items.some(item=>Number(item.quantity||0)<=0)){
      showError("Todas las cantidades deben ser mayores a cero.");
      return;
    }

    if(invalidNegative){
      showError("Uno o más productos quedarían con stock negativo.");
      return;
    }

    try{
      setSaving(true);

      const response=await fetch("/api/inventory/adjustments/",{
        method:"POST",
        headers:{
          Accept:"application/json",
          "Content-Type":"application/json",
          Authorization:`Bearer ${token}`
        },
        body:JSON.stringify({
          empresa_id:empresaId,
          tipo,
          reason:reason.trim(),
          items:items.map(item=>({
            product_id:item.product_id,
            quantity:item.quantity
          }))
        })
      });

      const data=await response.json();

      if(!response.ok){
        const error=data?.error||data?.detail;

        if(typeof error==="object"){
          showError(Object.values(error).flat().join(" "));
        }else{
          showError(error||"No se pudo registrar el movimiento.");
        }

        return;
      }

      showSuccess(data.mensaje||"Movimiento registrado correctamente.");

      onCreated?.(data);

    }catch{
      showError("Error de conexión al registrar el movimiento.");
    }finally{
      setSaving(false);
    }
  };

  const totalProductos=items.length;

  const totalCantidad=useMemo(()=>{
    return items.reduce(
      (sum,item)=>sum+Number(item.quantity||0),
      0
    );
  },[items]);

  const menuProps={
    disableScrollLock:true,
    PaperProps:{
      elevation:0,
      sx:{
        bgcolor:dashboardTheme.card_background,
        color:dashboardTheme.text_color,
        border:`1px solid ${dashboardTheme.border_color}`,
        boxShadow:"none"
      }
    }
  };

  return(
    <Box sx={{
      minHeight:"100vh",
      bgcolor:dashboardTheme.dashboard_background,
      display:"flex",
      color:dashboardTheme.text_color,
      overflowX:"hidden"
    }}>
      <Sidebar open={sidebarOpen} onClose={()=>setSidebarOpen(false)}/>

      <Box sx={{
        flex:1,
        minWidth:0,
        minHeight:"100vh",
        display:"flex",
        flexDirection:"column"
      }}>
        <TopBar
          sidebarOpen={sidebarOpen}
          onToggleSidebar={()=>setSidebarOpen(prev=>!prev)}
        />

        <Box component="main" sx={{flex:1,width:"100%",p:{xs:1.5,sm:2,md:3}}}>
          <Box sx={{width:{xs:"100%",lg:"94%"},mx:"auto"}}>

            <Box sx={{
              display:"flex",
              alignItems:{xs:"stretch",sm:"center"},
              justifyContent:"space-between",
              flexDirection:{xs:"column",sm:"row"},
              gap:1.5,
              mb:2
            }}>
              <Box>
                <Typography sx={{
                  fontSize:{xs:24,sm:26,md:30},
                  fontWeight:600,
                  color:dashboardTheme.primary_color
                }}>
                  Registrar movimiento de inventario
                </Typography>

                <Typography sx={{
                  mt:.4,
                  fontSize:13.5,
                  color:dashboardTheme.muted_text_color
                }}>
                  Añade los productos y cantidades que formarán parte del ajuste.
                </Typography>
              </Box>

              <Button
                variant="outlined"
                onClick={onBack}
                sx={{
                  minHeight:40,
                  px:2.2,
                  borderRadius:"10px",
                  textTransform:"none",
                  fontWeight:700,
                  color:dashboardTheme.button_color,
                  borderColor:dashboardTheme.button_color,
                  boxShadow:"none",
                  "&:hover":{
                    borderColor:dashboardTheme.button_color,
                    boxShadow:"none"
                  }
                }}
              >
                Regresar
              </Button>
            </Box>

            <Paper elevation={0} sx={{
              borderRadius:"14px",
              border:`1px solid ${dashboardTheme.border_color}`,
              bgcolor:dashboardTheme.card_background,
              overflow:"hidden",
              boxShadow:"none"
            }}>
              <Box sx={{
                px:{xs:1.5,md:2},
                py:1.4,
                borderBottom:`1px solid ${dashboardTheme.border_color}`,
                bgcolor:dashboardTheme.theme_mode==="dark"
                  ?"rgba(255,255,255,0.025)"
                  :"rgba(17,24,39,0.02)"
              }}>
                <Typography sx={{
                  fontSize:{xs:20,md:23},
                  fontWeight:700,
                  color:dashboardTheme.primary_color
                }}>
                  Información del movimiento
                </Typography>
              </Box>

              <Box sx={{p:{xs:1.5,md:2}}}>
                <Box sx={{
                  borderTop:`1px solid ${dashboardTheme.border_color}`,
                  borderLeft:`1px solid ${dashboardTheme.border_color}`,
                  borderRight:`1px solid ${dashboardTheme.border_color}`,
                  borderRadius:"10px",
                  overflow:"hidden"
                }}>
                  <Row label="Fecha" dashboardTheme={dashboardTheme}>
                    <TextField
                      size="small"
                      value={fecha}
                      InputProps={{readOnly:true}}
                      sx={{width:{xs:"100%",md:340},...fieldSx(dashboardTheme)}}
                    />
                  </Row>

                  <Row label="Responsable" dashboardTheme={dashboardTheme}>
                    <TextField
                      size="small"
                      value={responsable}
                      InputProps={{readOnly:true}}
                      sx={{width:{xs:"100%",md:340},...fieldSx(dashboardTheme)}}
                    />
                  </Row>

                  <Row label="Tipo de movimiento" dashboardTheme={dashboardTheme}>
                    <TextField
                      select
                      size="small"
                      value={tipo}
                      onChange={e=>setTipo(e.target.value)}
                      SelectProps={{MenuProps:menuProps}}
                      sx={{width:{xs:"100%",md:340},...fieldSx(dashboardTheme)}}
                    >
                      <MenuItem value="positivo">Aumentar stock</MenuItem>
                      <MenuItem value="negativo">Disminuir stock</MenuItem>
                    </TextField>
                  </Row>

                  <Row label="Motivo" dashboardTheme={dashboardTheme}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      value={reason}
                      onChange={e=>setReason(e.target.value)}
                      placeholder="Ej. Conteo físico, corrección de inventario..."
                      sx={fieldSx(dashboardTheme)}
                    />
                  </Row>
                </Box>
              </Box>
            </Paper>

            <Paper elevation={0} sx={{
              mt:2,
              borderRadius:"14px",
              border:`1px solid ${dashboardTheme.border_color}`,
              bgcolor:dashboardTheme.card_background,
              overflow:"hidden",
              boxShadow:"none"
            }}>
              <Box sx={{
                px:{xs:1.5,md:2},
                py:1.4,
                borderBottom:`1px solid ${dashboardTheme.border_color}`,
                bgcolor:dashboardTheme.theme_mode==="dark"
                  ?"rgba(255,255,255,0.025)"
                  :"rgba(17,24,39,0.02)"
              }}>
                <Typography sx={{
                  fontSize:{xs:20,md:23},
                  fontWeight:700,
                  color:dashboardTheme.primary_color
                }}>
                  Productos
                </Typography>
              </Box>

              <Box sx={{p:{xs:1.5,md:2}}}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={products}
                  value={selectedProduct}
                  inputValue={productSearch}
                  loading={loadingProducts}
                  onInputChange={(_event,value,reasonChange)=>{
                    if(reasonChange==="input"){
                      setProductSearch(value);
                    }

                    if(reasonChange==="clear"){
                      setProductSearch("");
                    }
                  }}
                  onChange={(_event,value)=>{
                    if(value){
                      addProduct(value);
                    }
                  }}
                  getOptionLabel={option=>{
                    if(!option)return"";

                    const code=option.code||option.barcode||"";

                    return code
                      ?`${option.name} - ${code}`
                      :option.name||"";
                  }}
                  isOptionEqualToValue={(option,value)=>
                    String(option?.id)===String(value?.id)
                  }
                  noOptionsText="No hay productos"
                  loadingText="Buscando..."
                  slotProps={{
                    paper:{
                      sx:{
                        bgcolor:dashboardTheme.card_background,
                        color:dashboardTheme.text_color,
                        border:`1px solid ${dashboardTheme.border_color}`,
                        boxShadow:"none",
                        "& .MuiAutocomplete-option":{
                          color:dashboardTheme.text_color
                        },
                        "& .MuiAutocomplete-option.Mui-focused":{
                          bgcolor:dashboardTheme.theme_mode==="dark"
                            ?"rgba(255,255,255,0.08)"
                            :"rgba(17,24,39,0.06)"
                        }
                      }
                    }
                  }}
                  renderOption={(props,option)=>(
                    <Box component="li" {...props} key={option.id}>
                      <Box sx={{minWidth:0}}>
                        <Typography sx={{
                          fontSize:14,
                          fontWeight:700,
                          color:dashboardTheme.text_color
                        }}>
                          {option.name}
                        </Typography>

                        <Typography sx={{
                          mt:.2,
                          fontSize:12.5,
                          color:dashboardTheme.muted_text_color
                        }}>
                          Stock: {formatNumber(option.stock)}
                          {option.code?` · Código: ${option.code}`:""}
                          {option.barcode?` · Barras: ${option.barcode}`:""}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  renderInput={params=>(
                    <TextField
                      {...params}
                      placeholder="Buscar producto..."
                      sx={fieldSx(dashboardTheme)}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment:(
                          <>
                            {loadingProducts?<CircularProgress size={18}/>:null}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
              </Box>

              <Box sx={dashboardTheme.tableStyles.tableArea}>
                <TableContainer sx={dashboardTheme.tableStyles.container}>
                  <Table size="small" sx={dashboardTheme.tableStyles.table}>
                    <TableHead>
                      <TableRow sx={dashboardTheme.tableStyles.headRow}>
                        <TableCell sx={{...dashboardTheme.tableStyles.headCell,minWidth:220}}>
                          Producto
                        </TableCell>

                        <TableCell sx={{...dashboardTheme.tableStyles.headCell,minWidth:130}}>
                          Código
                        </TableCell>

                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>
                          Stock actual
                        </TableCell>

                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>
                          Cantidad
                        </TableCell>

                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>
                          Stock resultante
                        </TableCell>

                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>
                          Acción
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {items.map(item=>{
                        const stockResultante=getStockResultante(item);
                        const invalid=stockResultante<0;

                        return(
                          <TableRow key={item.uid} sx={dashboardTheme.tableStyles.bodyRow}>
                            <TableCell>
                              <Typography sx={{
                                fontSize:14,
                                fontWeight:700,
                                color:dashboardTheme.text_color
                              }}>
                                {item.name}
                              </Typography>

                              {item.barcode&&(
                                <Typography sx={{
                                  mt:.2,
                                  fontSize:12,
                                  color:dashboardTheme.muted_text_color
                                }}>
                                  Barras: {item.barcode}
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell>
                              <Typography sx={{
                                fontSize:13.5,
                                color:dashboardTheme.text_color
                              }}>
                                {item.code||"-"}
                              </Typography>

                              {item.codigo_auxiliar&&(
                                <Typography sx={{
                                  mt:.2,
                                  fontSize:12,
                                  color:dashboardTheme.muted_text_color
                                }}>
                                  Aux: {item.codigo_auxiliar}
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell align="right">
                              <Typography sx={{
                                fontSize:14,
                                fontWeight:600,
                                color:dashboardTheme.text_color
                              }}>
                                {formatNumber(item.stock)}
                              </Typography>
                            </TableCell>

                            <TableCell align="right">
                              <TextField
                                size="small"
                                type="number"
                                value={item.quantity}
                                onChange={e=>updateQuantity(item.uid,e.target.value)}
                                inputProps={{
                                  min:"0.000001",
                                  step:"0.000001",
                                  inputMode:"decimal"
                                }}
                                error={Number(item.quantity||0)<=0||invalid}
                                sx={{
                                  width:110,
                                  ...fieldSx(dashboardTheme)
                                }}
                              />
                            </TableCell>

                            <TableCell align="right">
                              <Typography sx={{
                                fontSize:14,
                                fontWeight:700,
                                color:invalid?"#dc2626":dashboardTheme.text_color
                              }}>
                                {formatNumber(stockResultante)}
                              </Typography>
                            </TableCell>

                            <TableCell align="right">
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={()=>removeItem(item.uid)}
                                sx={{
                                  minHeight:34,
                                  px:1.4,
                                  borderRadius:"9px",
                                  textTransform:"none",
                                  fontWeight:700,
                                  color:"#dc2626",
                                  borderColor:"#dc2626",
                                  boxShadow:"none",
                                  "&:hover":{
                                    borderColor:"#dc2626",
                                    bgcolor:"rgba(220,38,38,0.08)",
                                    boxShadow:"none"
                                  }
                                }}
                              >
                                Quitar
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}

                      {items.length===0&&(
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={dashboardTheme.tableStyles.emptyCell}>
                            Busca productos y añádelos al movimiento
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box sx={{
                p:{xs:1.5,md:2},
                borderTop:`1px solid ${dashboardTheme.border_color}`,
                display:"flex",
                justifyContent:"space-between",
                alignItems:{xs:"stretch",sm:"center"},
                flexDirection:{xs:"column",sm:"row"},
                gap:1.5
              }}>
                <Box>
                  <Typography sx={{
                    fontSize:13.5,
                    color:dashboardTheme.muted_text_color
                  }}>
                    Productos:{" "}
                    <Box component="span" sx={{fontWeight:700,color:dashboardTheme.text_color}}>
                      {totalProductos}
                    </Box>
                  </Typography>

                  <Typography sx={{
                    mt:.25,
                    fontSize:13.5,
                    color:dashboardTheme.muted_text_color
                  }}>
                    Cantidad total:{" "}
                    <Box component="span" sx={{fontWeight:700,color:dashboardTheme.text_color}}>
                      {formatNumber(totalCantidad)}
                    </Box>
                  </Typography>
                </Box>

                <Box sx={{
                  display:"flex",
                  justifyContent:"flex-end",
                  gap:1
                }}>
                  <Button
                    variant="outlined"
                    disabled={saving}
                    onClick={onBack}
                    sx={{
                      borderRadius:"10px",
                      textTransform:"none",
                      fontWeight:700,
                      color:dashboardTheme.button_color,
                      borderColor:dashboardTheme.button_color,
                      boxShadow:"none",
                      "&:hover":{
                        borderColor:dashboardTheme.button_color,
                        boxShadow:"none"
                      }
                    }}
                  >
                    Cancelar
                  </Button>

                  <Button
                    variant="contained"
                    disabled={!canSubmit||saving}
                    onClick={save}
                    sx={dashboardTheme.buttonStyles.contained}
                  >
                    {saving?"Registrando...":"Registrar movimiento"}
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}